import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { AssetDTO, CreateScheduleFormRequestDTO, CreateScheduleFormResponseDTO, EntityRefUtil, FormElementKind } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { FormProvider, useForm } from 'react-hook-form';

import { SaveChangesDialog } from '@/components/common/save-changes-dialog';
import { initializeTableInitialValues } from '@/components/form/elements/table';
import { FormElementsRenderer, FormRendererData } from '@/components/form/form-elements-renderer';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import CloseIcon from '@/components/icons/close';
import { ReadOnlyField } from '@/components/ui/read-only-field';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { checkKeys } from '@/queries/checks';
import { getFormByIdQueryOptions } from '@/queries/forms';
import { activityKeys } from '@/queries/inspections';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useIsAWA } from '@/utils/awa';

export default function CreateScheduleRecordDrawer({
  formId,
  assetId,
  checkId,
  scheduleCheck,
  open,
  onOpenChange,
  onSuccess,
}: {
  formId?: string;
  assetId: string;
  checkId?: string;
  scheduleCheck?: { label: string };
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  onSuccess?: (response: CreateScheduleFormResponseDTO) => void;
}) {
  const toast = useToast();
  const { isAWA } = useIsAWA();
  const queryClient = useQueryClient();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isConfirmSaveDirtyChangesOpen, setIsConfirmSaveDirtyChangesOpen] = useState(false);
  const [isSheetAnimationComplete, setIsSheetAnimationComplete] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetDTO>();

  const { data: asset, isLoading: assetLoading } = useQuery({ ...getAssetByIdQueryOptions({ assetId, isAWA }), enabled: !!assetId && open });

  const { data: formData, isLoading: formLoading } = useQuery({ ...getFormByIdQueryOptions({ formId, isAWA }), enabled: !!formId && open });
  const form = formData?.form ?? null;

  useEffect(() => {
    if (asset && !assetLoading) {
      setSelectedAsset(asset);
    }
  }, [asset, assetLoading]);

  const defaultFormValues = useMemo(() => {
    if (!form?.elements) return {};

    return form.elements.reduce(
      (acc, element) => ({
        ...acc,
        ...(element.kind === FormElementKind.Table
          ? {
              [element.id]: [[...initializeTableInitialValues(element)]],
            }
          : {
              [element.id]: undefined,
            }),
      }),
      {} as FormRendererData,
    );
  }, [form]);

  const formInstance = useForm<FormRendererData>({
    defaultValues: defaultFormValues,
    mode: 'onBlur',
  });

  // useLayoutEffect ensures state is reset synchronously before paint,
  // preventing the confirmation dialog from flashing when drawer reopens
  useLayoutEffect(() => {
    if (open) {
      formInstance.reset(defaultFormValues);
      setIsFormDirty(false);
      setIsConfirmSaveDirtyChangesOpen(false);
    }
  }, [open, defaultFormValues, formInstance]);

  useEffect(() => {
    if (formInstance.formState.isDirty) {
      setIsFormDirty(formInstance.formState.isDirty);
    }
  }, [formInstance.formState.isDirty]);

  const { mutateAsync: createScheduleForm, isPending: isSubmitting } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!form?.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      const submittableElements = form.elements;

      const payload: CreateScheduleFormRequestDTO = {
        assetId: assetId,
        formRevisionId: form.latestRevisionId,
        data: Object.entries(data)
          .filter(([key]) => submittableElements.some((element) => element.id === key))
          .map(([key, value]) => ({
            elementId: key,
            value: value === undefined || value === '' ? null : value,
            kind: form.elements.find((element) => element.id === key)!.kind as CreateScheduleFormRequestDTO['data'][number]['kind'],
          })),
        ...(checkId ? { checkId } : {}),
      };

      const response = isAWA ? await AWAApiV2Service.CreateScheduleFormSubmission(payload) : await ApiService.CreateScheduleForm(payload);
      return await response.json();
    },
    onError: async (error: Error) => {
      if (error instanceof HTTPError) {
        try {
          const parsedError = await error.response.json();
          toast.error(parsedError?.message || 'Unable to save changes. Please try again.');
        } catch {
          toast.error('Unable to save changes. Please try again.');
        }
      } else {
        toast.error('Unable to save changes. Please try again.');
      }
    },
    onSuccess: async (createScheduleFormResponse) => {
      toast.success('Schedule form record created');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(assetId) }),
        queryClient.invalidateQueries({ queryKey: checkKeys.all }),
      ]);
      onSuccess?.(createScheduleFormResponse);
      onOpenChange?.(false, true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const handleSubmitForm = useCallback(
    async (data: FormRendererData) => {
      await createScheduleForm(data);
    },
    [createScheduleForm],
  );

  function handleError() {
    const formState = formInstance.formState;
    toast.error('The record has invalid or incomplete data. Please check.');
    const fieldNames = Object.keys(formState.errors);
    if (fieldNames.length > 0) {
      const firstFieldWithError = document.getElementById(fieldNames[0]);
      if (firstFieldWithError) {
        firstFieldWithError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    throw new Error('The record has invalid or incomplete data. Please check.');
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen && isSubmitting) return;
        onOpenChange?.(newOpen);
      }}>
      <SheetContent
        side={'right'}
        className={isAWA ? 'mx-auto h-full w-full sm:max-w-[800px]' : ''}
        hideCloseIcon
        onInteractOutside={(e) => {
          if (isSubmitting) {
            e.preventDefault();
            return;
          }
          if (isFormDirty) {
            e.preventDefault();
            setIsConfirmSaveDirtyChangesOpen(true);
          }
          // If not dirty and not submitting, allow the close to proceed
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onCloseAutoFocus={() => {
          setIsSheetAnimationComplete(false);
        }}
        onAnimationEnd={() => {
          if (open) {
            setIsSheetAnimationComplete(true);
          }
        }}>
        <VisuallyHidden>
          <SheetTitle>Create Schedule Form Record</SheetTitle>
          <SheetDescription>Fill out the form to create a new schedule form record</SheetDescription>
        </VisuallyHidden>
        <SheetHeader
          className={isAWA ? 'border-grey-3 m-0 border-b-2 p-0' : 'border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white'}>
          {isAWA ? (
            <div className="flex h-[80px] w-full flex-row items-center justify-between gap-2 bg-white px-4 py-[25px]">
              <div
                className={`mx-auto flex w-full flex-row items-center gap-2 p-0 ${isSubmitting ? 'pointer-events-none opacity-50' : ''}`}
                onClick={() => {
                  if (isSubmitting) return;
                  if (isFormDirty) {
                    setIsConfirmSaveDirtyChangesOpen(true);
                  } else {
                    onOpenChange?.(false);
                  }
                }}>
                <ArrowLeftIcon size={30} />
                <div className="text-default-text text-lg font-medium">Create Schedule Form Record</div>
              </div>
              <div>
                <Button
                  variant="ghost"
                  className={`text-default-green text-lg font-medium`}
                  loading={isSubmitting}
                  onClick={() => {
                    formInstance
                      .handleSubmit(handleSubmitForm, handleError)()
                      .then(() => {
                        onOpenChange?.(false, true);
                      })
                      .catch(() => {
                        onOpenChange?.(true);
                      });
                  }}
                  disabled={assetLoading || formLoading || !isFormDirty}>
                  Submit
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="line-clamp-1 text-lg font-semibold">
                Create {form?.name ?? 'Schedule Form Record'} {asset && `- ${EntityRefUtil.createAssetLabel(asset)}`}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="success"
                  loading={isSubmitting}
                  onClick={() => {
                    formInstance
                      .handleSubmit(handleSubmitForm, handleError)()
                      .then(() => {
                        onOpenChange?.(false, true);
                      })
                      .catch(() => {
                        onOpenChange?.(true);
                      });
                  }}
                  disabled={assetLoading || formLoading || !isFormDirty}>
                  Save
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (isFormDirty) {
                      setIsConfirmSaveDirtyChangesOpen(true);
                    } else {
                      onOpenChange?.(false);
                    }
                  }}>
                  <CloseIcon className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            </>
          )}
        </SheetHeader>
        <div className="flex flex-col overflow-y-auto">
          {!isSheetAnimationComplete || assetLoading || formLoading ? (
            <div className="flex h-screen w-screen items-center justify-center">
              <div className="border-default-grey-2 border-t-default-blue size-[30px] animate-spin rounded-full border-4" />
            </div>
          ) : (
            <div className="flex h-full w-full flex-col">
              <div className="w-full px-6 pt-4 pb-6">
                <div className="relative flex h-full w-full flex-col">
                  {selectedAsset && form ? (
                    <FormProvider {...formInstance}>
                      <form className="flex flex-col gap-2">
                        {scheduleCheck && (
                          <ReadOnlyField label="Schedule Check">
                            <span className="text-sm">{scheduleCheck.label}</span>
                          </ReadOnlyField>
                        )}
                        <FormElementsRenderer asset={selectedAsset} form={form} readonly={false} elements={form.elements} />
                      </form>
                    </FormProvider>
                  ) : !selectedAsset ? (
                    <div className="text-secondary-text mt-4 flex h-full items-center justify-center">No asset found.</div>
                  ) : (
                    <div className="text-secondary-text flex h-full items-center justify-center">Error loading form data.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>

      <SaveChangesDialog
        open={!!open && isConfirmSaveDirtyChangesOpen}
        onOpenChange={(newOpen) => {
          if (!newOpen && isSubmitting) return;
          setIsConfirmSaveDirtyChangesOpen(newOpen);
        }}
        saveCallback={() => {
          formInstance
            .handleSubmit(handleSubmitForm, handleError)()
            .then(() => onOpenChange?.(false, true))
            .catch(() => onOpenChange?.(true));
          setIsConfirmSaveDirtyChangesOpen(false);
        }}
        discardCallback={() => {
          setIsConfirmSaveDirtyChangesOpen(false);
          onOpenChange?.(false);
        }}
        loading={isSubmitting}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </Sheet>
  );
}
